import { useCallback, useState } from 'react';
import { useFig } from '../../session/FigProvider.js';
import type { AgencyUser, UserRole } from '../../core/api/types.js';
import { USER_ROLES } from '../../core/api/types.js';
import { FigApiError } from '../../core/api/errors.js';
import { EmptyState, ErrorState, RowSkeleton } from '../../components/States.js';
import { Dialog, Field } from '../../components/Dialog.js';
import { useResource } from './useResource.js';

/**
 * Agency staff: invite, disable, revoke a pending invitation.
 *
 * Two contract rules from the API reference are enforced in the UI rather than discovered as
 * errors: you cannot disable yourself, and DELETE only revokes a *pending* invitation — once
 * somebody has accepted, their id is referenced by assignments and the access log, so they are
 * disabled instead. Offering a delete that will 409 is worse than not offering it.
 */
export function Team() {
  const { client } = useFig();
  const load = useCallback(() => client.listUsers({ sortBy: 'Name', sortDirection: 'Asc' }), [client]);
  const { data: users, error, busy, reload } = useResource<readonly AgencyUser[]>(load);
  const [inviting, setInviting] = useState(false);

  if (error !== null) return <ErrorState error={error} onRetry={() => void reload()} />;

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Team</h1>
        <button className="btn btn-primary btn-inline" onClick={() => setInviting(true)}>
          Invite someone
        </button>
      </div>

      {users === null && busy && <RowSkeleton count={4} />}

      {users?.length === 0 && (
        <EmptyState
          title="It’s just you so far"
          body="Invite the people who’ll be reviewing calls. They’ll get an email with a link — no password to set up."
        />
      )}

      {users && users.length > 0 && (
        <table className="table">
          <caption className="sr-only">Agency staff, {users.length} people</caption>
          <thead>
            <tr><th scope="col">Name</th><th scope="col">Email</th><th scope="col">Role</th>
              <th scope="col">Status</th><th scope="col"><span className="sr-only">Actions</span></th></tr>
          </thead>
          <tbody>
            {users.map((u) => <TeamRow key={u.id} user={u} onChanged={() => void reload()} />)}
          </tbody>
        </table>
      )}

      <InviteDialog open={inviting} onClose={() => setInviting(false)} onInvited={() => { setInviting(false); void reload(); }} />
    </>
  );
}

function TeamRow({ user, onChanged }: { user: AgencyUser; onChanged: () => void }) {
  const { client, session } = useFig();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : 'That did not work.'); }
    finally { setBusy(false); }
  }

  // The API refuses to let anyone disable themselves. Showing the button and letting them
  // find out is worse than not offering it, so the session's own user id decides.
  const isSelf = session.userId !== null && session.userId === user.id;
  const status = user.disabled ? 'Disabled' : user.pending ? 'Invited' : 'Active';

  return (
    <tr>
      <th scope="row" className="cell-name">{user.name}</th>
      <td>{user.email}</td>
      <td>{user.role}</td>
      <td><span className={`pill pill-${status.toLowerCase()}`}>{status}</span></td>
      <td className="cell-actions">
        {user.pending ? (
          // DELETE revokes a pending invitation. 409 once accepted, so it is only offered here.
          <button className="btn btn-small" disabled={busy}
            onClick={() => void act(() => client.revokeInvitation(user.id))}>
            Revoke invite
          </button>
        ) : (
          <button className="btn btn-small" disabled={busy || isSelf}
            onClick={() => void act(() => client.setUserDisabled(user.id, !user.disabled))}
            title={isSelf ? 'You can’t disable your own account' : undefined}>
            {user.disabled ? 'Re-enable' : 'Disable'}
          </button>
        )}
        {error && <span className="hint hint-error" role="alert">{error}</span>}
      </td>
    </tr>
  );
}

function InviteDialog({ open, onClose, onInvited }: {
  open: boolean; onClose: () => void; onInvited: () => void;
}) {
  const { client } = useFig();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('Individual');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await client.inviteUser({ email: email.trim(), name: name.trim(), role });
      setEmail(''); setName(''); setRole('Individual');
      onInvited();
    } catch (err) {
      // Clerk allows 100 invitations an hour and the API passes its Retry-After straight
      // through. Saying so is more use than "something went wrong".
      setError(err instanceof FigApiError && err.isRateLimited
        ? 'That’s a lot of invitations at once. Wait a few minutes and send the rest.'
        : err instanceof Error ? err.message : 'That did not work.');
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Invite someone to your team">
      <form onSubmit={submit} noValidate>
        <Field id="invite-name" label="Their name" value={name} autoComplete="name"
          onChange={(e) => setName(e.target.value)} required />
        <Field id="invite-email" label="Email address" type="email" value={email}
          autoComplete="email" inputMode="email" onChange={(e) => setEmail(e.target.value)} required
          hint="They’ll get a link. There’s no password to set up." />
        <div className="field">
          <label className="field-label" htmlFor="invite-role">What they can do</label>
          <select id="invite-role" className="input" value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}>
            {USER_ROLES.map((r) => (
              <option key={r} value={r}>
                {r === 'Admin' ? 'Admin — everything, including this page'
                  : r === 'QaReviewer' ? 'QA reviewer — read calls and assign them'
                  : 'Individual — read calls and assign them'}
              </option>
            ))}
          </select>
        </div>
        {error && <p className="hint hint-error" role="alert">{error}</p>}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-inline" disabled={busy || !email.trim() || !name.trim()}>
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
